/* ===========================================================================
   Generic sticky add-to-cart, for every product page that does not already
   have one.

   WHY THIS EXISTS
   The six bespoke buy boxes (Solar Cam Pro 3.0, 4G, Lumis, Home Guardian,
   DoorMate and the advertorial) render their own sticky bar out of the kc3
   buy box, complete with per-product tier pricing. The other ~39 active
   products run stock templates -- sc3-0-bundles, home-guardian-bundles,
   geotag, key-rings, fortress-guardian-max-v1 and more -- and have nothing.
   Those pages carry roughly a quarter of 30-day revenue.

   Copying the kc3 bar was not an option: it is wired to tier pricing and card
   compatibility that a GeoTag or a 6-pack has no concept of. So this drives
   the theme's OWN product form instead, which means it works on any template
   without per-product configuration.

   DESIGN NOTES
   - It never builds its own cart request. It clicks the theme's real submit
     button, so variant selection, quantity, the disabled/enabled id inputs,
     the loading state and the drawer opening are all the theme's own logic.
     Anything the real button does right, this does right.
   - The price is MIRRORED from whatever the theme already renders, rather
     than computed. That keeps it correct across variant switches, discounts
     and currency without this file knowing anything about pricing.
   - It stands down entirely where a kc3 sticky already exists, so we never
     end up with two bars.
   =========================================================================== */
(function () {
  'use strict';

  if (window.__kcSticky) return;          /* the snippet renders once, but be safe */
  window.__kcSticky = true;

  /* PRODUCT PAGES ONLY (30 Sep 2026).
     This file was always meant for product pages, but nothing enforced it, and
     mainForm() below simply takes the first /cart/add form on the page. On the
     home page that is the first card in the featured-collection row, so the bar
     rendered "KeldCo(tm) - $329.00 - ADD TO CART" on mobile and, when tapped,
     silently added a Solar Cam Pro 3.0 the shopper had never chosen. The title
     read "KeldCo(tm)" rather than the product name because the card's section
     has no h1 and productTitle() falls back to document.title, so they could
     not even see what they were buying.
     Collections and search built the bar too; only home ever showed it.
     Matched loosely rather than anchored: a product can also be reached at
     /collections/<c>/products/<p>, and although this store currently redirects
     that to /products/<p>, an anchored test would silently kill the bar on all
     ~39 stock product pages if that ever changed. */
  if (!/\/products\//.test(window.location.pathname)) return;

  var BAR_ID = 'kc-sticky-bar';

  /* ------------------------------------------------------------- discovery */

  /* The six bespoke pages already have one. Leave them completely alone. */
  function hasBespokeSticky() {
    return !!document.querySelector('#stickyBtn, .kc3 .sticky');
  }

  /* The main product form, not the quick-buy in a card and not anything
     inside the cart drawer. */
  function mainForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      var id = f.getAttribute('id') || '';
      if (/quick-?buy/i.test(id)) continue;
      if (f.closest && f.closest('#cart-drawer')) continue;
      if (f.querySelector('button[type="submit"]')) return f;
    }
    return null;
  }

  function realButton(form) {
    return form.querySelector('button[type="submit"]');
  }

  /* Whatever the theme renders as the price, wherever it renders it. Look
     inside the form's own section first so a "you may also like" price can
     never be picked up by mistake. */
  function priceSource(form) {
    var scope = form.closest('.shopify-section') || document;
    return scope.querySelector('price-list, .price-list, [class*="price-list"]') ||
           scope.querySelector('.price, [class*="product-price"]');
  }

  function productTitle(form) {
    var scope = form.closest('.shopify-section') || document;
    var h = scope.querySelector('h1') || document.querySelector('h1');
    return h ? h.textContent.trim() : (document.title || '').split('|')[0].trim();
  }

  function productImage(form) {
    var scope = form.closest('.shopify-section') || document;
    var img = scope.querySelector('img[src*="/products/"], img[srcset*="/products/"]') ||
              document.querySelector('img[src*="/products/"]');
    if (img) return img.currentSrc || img.src;
    var og = document.querySelector('meta[property="og:image"]');
    return og ? og.getAttribute('content') : '';
  }

  /* ---------------------------------------------------------------- build */

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function build(form, btn) {
    var bar = el('div', 'kc-sticky-bar');
    bar.id = BAR_ID;

    var src = productImage(form);
    if (src) {
      var img = el('img', 'kc-sticky-bar__img');
      img.src = src; img.alt = ''; img.loading = 'lazy';
      bar.appendChild(img);
    }

    var mid = el('div', 'kc-sticky-bar__mid');
    mid.appendChild(el('span', 'kc-sticky-bar__title', productTitle(form)));
    var price = el('span', 'kc-sticky-bar__price');
    mid.appendChild(price);
    bar.appendChild(mid);

    var cta = el('button', 'kc-sticky-bar__cta', 'ADD TO CART');
    cta.type = 'button';
    bar.appendChild(cta);

    /* Drive the theme's own button. Everything it does, we inherit. */
    cta.addEventListener('click', function () {
      if (btn.disabled) return;
      btn.click();
    });

    /* Mirror the price, and keep mirroring it when the theme rewrites it on a
       variant change. */
    var source = priceSource(form);
    function syncPrice() {
      if (!source) { price.hidden = true; return; }
      var t = (source.textContent || '').replace(/\s+/g, ' ').trim();
      if (t && price.textContent !== t) price.textContent = t;
      price.hidden = !t;
    }
    syncPrice();
    if (source) {
      new MutationObserver(syncPrice).observe(source, {
        childList: true, subtree: true, characterData: true
      });
    }

    /* Sold out, or otherwise unavailable: follow the real button rather than
       offering an action that cannot succeed. */
    function syncState() {
      var off = btn.disabled || btn.getAttribute('aria-disabled') === 'true';
      cta.disabled = !!off;
      bar.classList.toggle('kc-sticky-bar--off', !!off);
    }
    syncState();
    new MutationObserver(syncState).observe(btn, {
      attributes: true, attributeFilter: ['disabled', 'aria-disabled']
    });

    document.body.appendChild(bar);
    return bar;
  }

  /* ------------------------------------------------------------ visibility */

  /* Show it once the real button has scrolled out of view, and never while
     the cart drawer is open -- kc-cart.js already flags that on <html>. */
  function watch(bar, btn) {
    var ticking = false;
    function sync() {
      ticking = false;
      var r = btn.getBoundingClientRect();
      var gone = r.bottom < 0 || r.top > (window.innerHeight || 0);
      var drawerOpen = document.documentElement.classList.contains('kc-drawer-open');
      bar.classList.toggle('kc-visible', gone && !drawerOpen);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sync);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    new MutationObserver(sync).observe(document.documentElement, {
      attributes: true, attributeFilter: ['class']
    });
    sync();
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    if (hasBespokeSticky()) return;
    if (document.getElementById(BAR_ID)) return;
    var form = mainForm();
    if (!form) return;                     /* not a product page */
    var btn = realButton(form);
    if (!btn) return;
    watch(build(form, btn), btn);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
